<template>
    <Disclosure as="nav" class="sticky top-0 z-50 bg-white/80 backdrop-blur-md shadow-sm ring-1 ring-slate-900/5" v-slot="{ open }">
        <div class="mx-auto max-w-7xl px-2 sm:px-6 lg:px-8">
            <div class="relative flex h-20 items-center justify-between">
                <div class="absolute inset-y-0 left-0 flex items-center sm:hidden">
                    <!-- Mobile menu button-->
                    <DisclosureButton
                        class="relative inline-flex items-center justify-center rounded-md p-2 text-slate-400 hover:bg-slate-700 hover:text-white focus:ring-2 focus:ring-white focus:outline-hidden focus:ring-inset">
                        <span class="absolute -inset-0.5"></span>
                        <span class="sr-only">Open main menu</span>
                        <Bars3Icon v-if="!open" class="block size-6" aria-hidden="true" />
                        <XMarkIcon v-else class="block size-6" aria-hidden="true" />
                    </DisclosureButton>
                </div>
                <div class="flex flex-1 items-center justify-center sm:items-stretch sm:justify-start">
                    <a href="/" class="flex shrink-0 items-center gap-2">
                        <img class="h-12 w-auto" src="/images/logo.png" alt="Sultana Vera logo" />
                    </a>
                    <div class="hidden sm:ml-6 sm:block">
                        <div class="flex space-x-4 h-full items-center">
                            <Menu as="div" class="relative flex justify-start items-center text-left">
                                <div v-for="item in navigation" :key="item.name">
                                    <router-link :to="item.href" v-if="!item.dropdown"
                                        :class="[
                                            item.current ? 'text-primary-600 after:w-full' : 'text-slate-700 hover:text-primary-600 after:w-0 hover:after:w-full',
                                            item.color ? item.color : '',
                                            'relative px-3 py-2 text-sm font-medium transition-colors after:absolute after:bottom-0 after:left-3 after:right-3 after:h-0.5 after:bg-primary-600 after:transition-all after:duration-300'
                                        ]" :aria-current="item.current ? 'page' : undefined">
                                        {{ item.name }}
                                        <ChevronDownIcon v-if="item.dropdown"
                                            class="inline-block ml-2 -mr-1 h-5 w-5 text-slate-800" aria-hidden="true" />
                                    </router-link>

                                    <div v-else class="relative">
                                        <MenuButton
                                            :class="[item.current ? 'text-primary-600' : 'text-slate-700 hover:text-primary-600', 'rounded-md px-3 py-2 text-sm font-medium transition-colors']"
                                            :aria-current="item.current ? 'page' : undefined">
                                            {{ item.name }}
                                            <ChevronDownIcon class="ml-2 -mr-1 h-5 w-5 text-slate-800 inline-block"
                                                aria-hidden="true" />
                                        </MenuButton>
                                        <Transition enter="transition ease-out duration-100"
                                            enter-from="transform opacity-0 scale-95"
                                            enter-to="transform opacity-100 scale-100"
                                            leave="transition ease-in duration-75"
                                            leave-from="transform opacity-100 scale-100"
                                            leave-to="transform opacity-0 scale-95">
                                            <MenuItems
                                                class="absolute right-0 mt-2 w-56 origin-top-right divide-y divide-slate-100 rounded-md bg-white shadow-lg ring-1 ring-slate-300 ring-opacity-5 focus:outline-none z-50">
                                                <div class="px-1 py-1">
                                                    <MenuItem v-slot="{ active }" v-for="subItem in item.dropdown.items"
                                                        :key="subItem.name">
                                                        <router-link :to="subItem.href" :class="[
                                                            active ? 'bg-slate-200 text-slate-700' : 'text-slate-900',
                                                            'group flex w-full items-center rounded-md px-2 py-2 text-sm'
                                                        ]">
                                                            {{ subItem.name }}
                                                        </router-link>
                                                    </MenuItem>
                                                </div>
                                            </MenuItems>
                                        </Transition>
                                    </div>
                                </div>
                            </Menu>
                        </div>
                    </div>
                </div>
                <div v-if="user">
                    <Menu as="div" class="relative flex justify-start items-center text-left">
                        <div>
                            <div class="relative">
                                <MenuButton
                                    class="flex items-center text-sm rounded-full focus:outline-hidden focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-slate-800">
                                    <img class="h-8 w-8 rounded-full" :src="user.avatar || '/images/default-avatar.png'"
                                        alt="User Avatar" />
                                    <ChevronDownIcon class="ml-2 -mr-1 h-5 w-5 text-slate-800 inline-block"
                                        aria-hidden="true" />
                                </MenuButton>
                                <Transition enter="transition ease-out duration-100"
                                    enter-from="transform opacity-0 scale-95" enter-to="transform opacity-100 scale-100"
                                    leave="transition ease-in duration-75" leave-from="transform opacity-100 scale-100"
                                    leave-to="transform opacity-0 scale-95">
                                    <MenuItems
                                        class="absolute right-0 mt-2 w-56 origin-top-right divide-y divide-slate-100 rounded-md bg-white shadow-lg ring-1 ring-slate-300 ring-opacity-5 focus:outline-none z-50">
                                        <div class="px-1 py-1">
                                            <MenuItem v-slot="{ active }" v-for="item in userDropdownItems"
                                                :key="item.name">
                                            <a :href="item.href" :class="[
                                                active ? 'bg-slate-200 text-slate-700' : 'text-slate-900',
                                                'group flex w-full items-center rounded-md px-2 py-2 text-sm'
                                            ]">
                                                <component :is="item.icon" class="mr-2 h-5 w-5 text-slate-500" />
                                                <span>{{ item.name }}</span>
                                            </a>
                                            </MenuItem>
                                        </div>
                                    </MenuItems>
                                </Transition>
                            </div>
                        </div>
                    </Menu>
                </div>
                <div v-else class="hidden sm:flex sm:items-center sm:justify-end">
                    <router-link to="/register" class="text-sm font-semibold text-white">
                        <Button variant="primary" size="sm" class="me-2 transition-all hover:shadow-lg hover:shadow-primary-500/25 active:scale-[0.98]">
                            Register
                        </Button>
                    </router-link>
                    <router-link to="/login" class="text-sm font-semibold text-primary">
                        <Button variant="outlined" size="sm">
                            Login
                        </Button>
                    </router-link>
                </div>
            </div>
        </div>

        <DisclosurePanel class="sm:hidden">
            <div class="space-y-1 px-2 pt-2 pb-3">
                <DisclosureButton v-for="item in navigation" :key="item.name" as="a" :href="item.href"
                    :class="[item.current ? 'bg-slate-900 text-white' : 'text-slate-800 hover:bg-slate-700 hover:text-white', 'block rounded-md px-3 py-2 text-base font-medium']"
                    :aria-current="item.current ? 'page' : undefined">{{ item.name }}</DisclosureButton>
            </div>
        </DisclosurePanel>
    </Disclosure>
</template>

<script setup>
import { Disclosure, DisclosureButton, DisclosurePanel, Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue';
import { 
    Bars3Icon,
    BellIcon,
    XMarkIcon,
    AcademicCapIcon,
    UserIcon,
    ArrowLeftEndOnRectangleIcon
} from '@heroicons/vue/24/outline';
import { ChevronDownIcon } from '@heroicons/vue/20/solid';
import Button from 'primevue/button';
import { useAuth } from '@/composables/useAuth';
import { onMounted } from 'vue';
import { getFeaturedCategories } from '@/services/categoryService';
import { ref } from 'vue';

const { getUser } = useAuth();
const user = ref(null);
const categories = ref([]);

const navigation = [
    {
        name: 'Programs', href: '#', current: false, dropdown: {
            items: []
        }
    },
    { name: 'Books', href: '/books', current: false, dropdown: false },
    { name: 'About Us', href: '/about-us', current: false, dropdown: false },
    { name: 'Testimonial', href: '/#testimonials', current: false, dropdown: false },
    { name: 'Contact Us', href: '/contact-us', current: false, dropdown: false },
    { name: 'Privacy Policy', href: '/privacy-policy', current: false, dropdown: false },
];

const userDropdownItems = [
    { name: 'Account', href: '/account', icon: UserIcon },
    { name: 'Learning', href: '/learning', icon: AcademicCapIcon },
    { name: 'Logout', href: '/logout', icon: ArrowLeftEndOnRectangleIcon }
];

onMounted(() => {
    getUser()
        .then(userData => {
            user.value = userData;
        })
        .catch(error => {
            // console.error('Error fetching user data:', error);
        });

    getFeaturedCategories().then(data => {
        categories.value = data.data;

        navigation[0].dropdown.items = categories.value.map(category => ({
            name: category.name,
            href: `/courses?category=${category.slug}`
        }));
    });
})
</script>